import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/toaster.jsx';
import useMe from '@/hooks/useMe.js';
import { ownerPagesVisible } from '@/lib/session.js';
import { ModelPick } from '@/components/account/ExperienceModelDialog.jsx';
import { stateExperienceRowModel, stateExperienceHours, stateRegistryMatch } from '@/services/deals.service.js';
import { getExperienceBacklog, buildExperienceBacklog, recheckExperienceSession } from '@/services/experience-backlog.service.js';

// TEMPORARY (David 2026-10-29): every blocked pilot card the exp-model-match
// merge left, one row each, the fix in the row. Saving a row rechecks its session
// (the same check the carrier send doors run) and moves to the next row. Remove
// this page, its route, routes/experience-backlog.routes.js,
// services/experience-backlog.service.js and the experience_backlog table once
// the backlog is gone.

const hrs = (v) => (v == null || v === '' ? 'blank' : String(v));
const errText = (e) => e?.body?.error || e?.message || 'failed';
const cell = 'border-b border-border px-2 py-2 align-top text-[12.5px] text-text';
const btn = 'inline-flex items-center rounded-[5px] border border-accent bg-accent px-2.5 py-1 text-[12px] text-white hover:brightness-110 disabled:opacity-50';

function Lines({ card }) {
  const sel = card.selection || {};
  if (card.kind === 'registry') return <span className="text-text-dim">aircraft, no pilot line</span>;
  const rows = sel.rows || [];
  if (!rows.length) return <span className="text-text-dim">{card.message}</span>;
  return (
    <ul className="space-y-0.5">
      {rows.map((r) => <li key={r.row_key || r.make_model}>{r.make_model}: {card.kind === 'disagree' ? hrs(r.value) : `${hrs(r.total_hours)} total, ${hrs(r.last_12_months)} last 12`}</li>)}
    </ul>
  );
}

function AircraftCell({ card }) {
  const sel = card.selection || {};
  if (card.kind === 'registry') return <span>{sel.tail}: not in the FAA registry{sel.serial ? `, serial ${sel.serial}` : ''}</span>;
  return (
    <ul className="space-y-0.5">
      {(sel.aircraft || []).map((a) => <li key={a.tail}>{a.tail}: {a.faa_model_name || 'no FAA model'}{a.faa_model_code ? ` (${a.faa_model_code})` : ''}</li>)}
    </ul>
  );
}

// Each fix saves through the door the deal page uses, then the row's session is rechecked.
function Fix({ card, onSaved, rowRef }) {
  const sel = card.selection || {};
  const [saving, setSaving] = useState(false);
  const [lineKey, setLineKey] = useState((sel.rows || []).length === 1 ? sel.rows[0].row_key : '');
  const [model, setModel] = useState(null);
  const [value, setValue] = useState(card.kind === 'blank' ? '' : null);
  const [reg, setReg] = useState((sel.candidates || []).length === 1 ? sel.candidates[0].registration : '');
  const run = async (fn) => { setSaving(true); try { await fn(); await onSaved(card); } catch (e) { toast.error(`Not saved: ${errText(e)}`); } finally { setSaving(false); } };

  if (card.kind === 'model') {
    // "Counts as this aircraft?" left 2026-10-31: the FAA-model family store is frozen (Admin > Aircraft models places AMIN models).
    const ready = lineKey && model;
    return (
      <div ref={rowRef} className="space-y-1.5">
        {(sel.rows || []).length > 1 ? (
          <select className="w-full rounded-[5px] border border-border bg-surface px-2 py-1 text-text" value={lineKey} onChange={(e) => setLineKey(e.target.value)}>
            <option value="">Which line?</option>
            {sel.rows.map((r) => <option key={r.row_key} value={r.row_key}>{r.make_model}</option>)}
          </select>
        ) : null}
        <ModelPick defaultMake={sel.aircraft?.[0]?.registry_make || ''} onPick={setModel} />
        <div>
          <button type="button" className={btn} disabled={!ready || saving} onClick={() => run(async () => {
            await stateExperienceRowModel({ phf_id: sel.phf_id, row_key: lineKey, faa_model_code: model.faa_model_code });
          })}>{saving ? 'Saving' : 'Save the line\'s FAA model'}</button>
        </div>
      </div>
    );
  }
  if (card.kind === 'blank') {
    const n = Number(value);
    const ok = value !== '' && Number.isFinite(n) && n >= 0;
    return (
      <div ref={rowRef} className="flex items-center gap-2">
        <span className="text-text-dim">{sel.field === 'total' ? 'Total' : 'Last 12 months'}</span>
        <input type="number" min="0" step="0.1" className="w-24 rounded-[5px] border border-border bg-surface px-2 py-1 text-text" value={value} onChange={(e) => setValue(e.target.value)} />
        <button type="button" className={btn} disabled={!ok || saving} onClick={() => run(() => stateExperienceHours({ phf_id: sel.phf_id, faa_model_code: sel.faa_model_code, field: sel.field, value: n }))}>{saving ? 'Saving' : 'Save'}</button>
      </div>
    );
  }
  if (card.kind === 'disagree') {
    return (
      <div ref={rowRef} className="flex flex-wrap items-center gap-2">
        <span className="text-text-dim">Which is his {sel.field === 'total' ? 'total' : 'last 12'}?</span>
        {(sel.values || []).map((v) => <label key={v}><input type="radio" checked={value === v} onChange={() => setValue(v)} /> {v}</label>)}
        <button type="button" className={btn} disabled={value === null || saving} onClick={() => run(() => stateExperienceHours({ phf_id: sel.phf_id, faa_model_code: sel.faa_model_code, field: sel.field, value }))}>{saving ? 'Saving' : 'Save'}</button>
      </div>
    );
  }
  if (card.kind === 'registry') {
    const c = sel.candidates || [];
    if (!c.length) return <div ref={rowRef} className="text-text-dim">{card.message}</div>;
    return (
      <div ref={rowRef} className="space-y-1">
        {c.map((x) => (
          <label key={x.registration} className="block"><input type="radio" checked={reg === x.registration} onChange={() => setReg(x.registration)} /> {x.registration}: {[x.year, x.make, x.model].filter(Boolean).join(' ')}</label>
        ))}
        <span className="mr-2 text-text-dim">Same airframe as {sel.tail}?</span>
        <button type="button" className={btn} disabled={!reg || saving} onClick={() => run(() => stateRegistryMatch({ tail: sel.tail, registration: reg }))}>{saving ? 'Saving' : 'Yes, same airframe'}</button>
      </div>
    );
  }
  return <div ref={rowRef} className="text-text-dim">{card.message}</div>;
}

export default function ExperienceBacklog() {
  const me = useMe();
  const [data, setData] = useState(null);
  const [focusIx, setFocusIx] = useState(0);
  const rowRefs = useRef([]);
  const load = useCallback(() => getExperienceBacklog().then(setData).catch((e) => toast.error(`The backlog could not be read: ${errText(e)}`)), []);
  useEffect(() => { load(); }, [load]);
  const running = !!data?.build?.running;
  // After a save the next row takes the saved row's place: scroll to it.
  useEffect(() => { rowRefs.current[focusIx]?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }); }, [data, focusIx]);

  const onSaved = useCallback(async (card) => {
    const ix = (data?.cards || []).findIndex((c) => c.key === card.key && c.session_id === card.session_id);
    try {
      const r = await recheckExperienceSession(card.session_id);
      const still = (r.cards || []).some((c) => c.key === card.key);
      if (r.error) toast.info(`Saved, but the recheck could not build this session: ${r.error}`);
      else if (still) toast.info('Saved. The send doors still raise this row: it needs another answer.');
      else toast.success('Saved. Cleared.');
    } catch (e) {
      toast.info(`Saved, but the recheck failed: ${errText(e)}`);
    }
    setFocusIx(Math.max(0, ix));
    await load();
  }, [data, load]);

  if (me && !ownerPagesVisible(me)) return <div className="p-4 text-sm text-text-muted">Only the owner sees this page.</div>;
  const cards = data?.cards || [];
  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold text-text">Experience backlog (temporary)</h2>
      <p className="mt-1 text-sm text-text-muted">Every pilot card the carrier send doors refuse on make and model hours, one row each. Save a row and its session is rechecked; the row clears when the doors stop refusing it.</p>
      <div className="mt-2 flex items-center gap-3 text-[12.5px] text-text">
        <span>{cards.length} rows, {data?.sessions_checked ?? 0} sessions checked{data?.sessions_failed?.length ? `, ${data.sessions_failed.length} could not be built` : ''}</span>
        {running ? <><span>Building: {data.build.done} of {data.build.total}</span><button type="button" className="rounded-[5px] border border-border px-2.5 py-1 text-[12px] text-text hover:bg-surface-strong" onClick={load}>Refresh progress</button></> : null}
        <button type="button" className={btn} disabled={running} onClick={() => buildExperienceBacklog().then(load).catch((e) => toast.error(`Build not started: ${errText(e)}`))}>
          Build the backlog (reads every open deal from Zoho, about 1,900 calls)
        </button>
      </div>
      <table className="mt-3 w-full border-collapse">
        <thead>
          <tr className="text-left text-[12px] text-text-dim">
            <th className="border-b border-border px-2 py-1">Session</th>
            <th className="border-b border-border px-2 py-1">Pilot</th>
            <th className="border-b border-border px-2 py-1">His line as stated</th>
            <th className="border-b border-border px-2 py-1">Aircraft FAA model</th>
            <th className="border-b border-border px-2 py-1">Fix</th>
          </tr>
        </thead>
        <tbody>
          {cards.map((c, i) => (
            <tr key={`${c.session_id}|${c.key}`}>
              <td className={cell}>{c.session_id.slice(0, 8)}<div className="text-text-dim">{c.account || ''}</div></td>
              <td className={cell}>{c.pilot || '(aircraft)'}</td>
              <td className={cell}><Lines card={c} /></td>
              <td className={cell}><AircraftCell card={c} /></td>
              <td className={cell}><Fix card={c} onSaved={onSaved} rowRef={(el) => { rowRefs.current[i] = el; }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {data && !cards.length && !running ? <p className="mt-3 text-sm text-text">No rows. {data.sessions_checked ? 'The backlog is clear.' : 'Build the backlog to list them.'}</p> : null}
    </div>
  );
}
